import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  ArrowLeft, DollarSign, AlertTriangle, Users, Package,
  Clock, TrendingUp, Truck, Plus, FileText, ArrowRightLeft, Check, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/components/job-dashboard/helpers';
import { LaborTab } from '@/components/job-dashboard/LaborTab';
import { MaterialTab } from '@/components/job-dashboard/MaterialTab';
import { EquipmentTab } from '@/components/job-dashboard/EquipmentTab';
import { PendingTab } from '@/components/job-dashboard/PendingTab';
import { ChangeOrdersTab } from '@/components/job-dashboard/ChangeOrdersTab';
import { CrossChargesTab } from '@/components/job-dashboard/CrossChargesTab';
import { MoveLaborDialog } from '@/components/job-dashboard/MoveLaborDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobDashboard() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [excludeNonBillable, setExcludeNonBillable] = useState(false);
  const [changeOrders, setChangeOrders] = useState([]);
  const [jobs, setJobs] = useState([]);

  // Labor move state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [selectedLaborIds, setSelectedLaborIds] = useState([]);
  const [moveTargetJobId, setMoveTargetJobId] = useState('');
  const [moveReason, setMoveReason] = useState('cross_charge');
  const [movingLabor, setMovingLabor] = useState(false);

  // Invoiced amount state
  const [editingInvoiced, setEditingInvoiced] = useState(false);
  const [invoicedInput, setInvoicedInput] = useState('');
  const isOfficeOrOwner = user?.role === 'OFFICE' || user?.role === 'OWNER';

  useEffect(() => {
    fetchJobDashboard();
    fetchChangeOrders();
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchJobDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/job/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch job dashboard:', error);
      navigate('/dashboard/recap');
    } finally {
      setLoading(false);
    }
  };

  const fetchChangeOrders = async () => {
    try {
      const response = await axios.get(`${API}/change-orders?job_id=${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChangeOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch change orders:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.filter(j => j.id !== id));
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  };

  const approvedCOAmount = useMemo(() => {
    return changeOrders.filter(co => co.status === 'APPROVED').reduce((sum, co) => sum + (co.costImpact || 0), 0);
  }, [changeOrders]);

  const pendingCOAmount = useMemo(() => {
    return changeOrders.filter(co => co.status === 'PENDING').reduce((sum, co) => sum + (co.costImpact || 0), 0);
  }, [changeOrders]);

  const handleCreateChangeOrderFromOverage = () => {
    if (data?.summary?.overageCharges?.length > 0) {
      sessionStorage.setItem('overageCharges', JSON.stringify({
        jobId: id, jobNumber: data.job.jobNumber,
        overageAmount: data.summary.overageAmount,
        charges: data.summary.overageCharges
      }));
      navigate('/dashboard/change-orders?createFromOverage=true');
    }
  };

  const handleMoveLaborEntries = async () => {
    if (!moveTargetJobId || selectedLaborIds.length === 0) {
      toast.error('Please select a target job and labor entries');
      return;
    }
    setMovingLabor(true);
    try {
      await axios.post(`${API}/labor-entries/bulk-move`, {
        laborEntryIds: selectedLaborIds, targetJobId: moveTargetJobId,
        reason: moveReason, sourceJobId: id
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Successfully moved ${selectedLaborIds.length} labor entries`);
      setMoveDialogOpen(false);
      setSelectedLaborIds([]);
      setMoveTargetJobId('');
      setMoveReason('cross_charge');
      fetchJobDashboard();
    } catch (error) {
      console.error('Failed to move labor entries:', error);
      toast.error(error.response?.data?.detail || 'Failed to move labor entries');
    } finally {
      setMovingLabor(false);
    }
  };

  const handleSaveInvoicedAmount = async () => {
    const amount = parseFloat(invoicedInput);
    if (isNaN(amount) || amount < 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await axios.patch(`${API}/jobs/${id}/invoiced-amount`, {
        invoicedAmount: amount
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invoiced amount updated');
      setEditingInvoiced(false);
      fetchJobDashboard();
    } catch (error) {
      toast.error('Failed to update invoiced amount');
    }
  };

  if (loading) return <div className='p-4 md:p-8'><div className='text-center text-slate-600'>Loading job dashboard...</div></div>;
  if (!data) return <div className='p-4 md:p-8'><div className='text-center text-slate-600'>Job not found</div></div>;

  const { job, summary, labor, material, equipment, pending } = data;

  // Non-billable calculations
  const nonBillableMaterialCharges = material?.entries?.filter(e => e.type === 'NON_BILLABLE')?.reduce((sum, e) => sum + (e.totalWithMarkup || e.amount || 0), 0) || 0;
  const nonBillablePendingAmount = pending?.entries?.filter(e => e.type === 'NON_BILLABLE')?.reduce((sum, e) => sum + (e.totalWithMarkup || e.approxAmount || 0), 0) || 0;

  // Adjusted values
  const adjustedMaterialCharges = excludeNonBillable ? (summary.materialCharges - nonBillableMaterialCharges) : summary.materialCharges;
  const adjustedPendingAmount = excludeNonBillable ? (summary.pendingPoAmount - nonBillablePendingAmount) : summary.pendingPoAmount;
  const adjustedTotalCharges = summary.laborCharges + adjustedMaterialCharges + (summary.equipmentCharges || 0);
  const adjustedTotalWithPending = adjustedTotalCharges + adjustedPendingAmount;
  const adjustedUtilizationPctWithPending = summary.poValue > 0 ? Math.round((adjustedTotalWithPending / summary.poValue) * 1000) / 10 : 0;
  const adjustedPoBalanceWithPending = summary.poValue - adjustedTotalWithPending;
  const adjustedIsOverPo = adjustedUtilizationPctWithPending >= 100;
  const adjustedIsNearPo = adjustedUtilizationPctWithPending >= 90 && adjustedUtilizationPctWithPending < 100;
  const overageAmount = summary.overageAmount || Math.max(0, adjustedTotalWithPending - summary.poValue);

  // Selected labor total for move dialog
  const selectedLaborAmount = data.labor?.entries?.filter(e => selectedLaborIds.includes(e.id)).reduce((sum, e) => sum + e.totalCharge, 0) || 0;

  return (
    <div className='p-4 md:p-8 space-y-6' data-testid='job-dashboard-page'>
      {/* Over Budget Alert Banner */}
      {adjustedIsOverPo && (
        <div className='bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-4' data-testid='over-budget-alert'>
          <div className='flex items-start gap-3'>
            <AlertTriangle className='w-6 h-6 text-red-600 flex-shrink-0 mt-0.5' />
            <div className='flex-1'>
              <h3 className='font-bold text-red-800 text-lg'>This Job is Over PO Utilization</h3>
              <p className='text-red-700 mt-1'>
                This job has exceeded its PO value by <span className='font-bold'>{formatCurrency(overageAmount)}</span>.
                A Change Order is required to cover the overage.
              </p>
              {summary.overageCharges?.length > 0 && (
                <div className='mt-3'>
                  <p className='text-sm text-red-600 font-medium mb-2'>
                    Charges that caused the overage ({summary.overageCharges.length} items):
                  </p>
                  <div className='max-h-32 overflow-y-auto bg-red-100 rounded p-2'>
                    {summary.overageCharges.slice(0, 5).map((charge, idx) => (
                      <div key={`overage-${idx}`} className='flex justify-between text-sm py-1 border-b border-red-200 last:border-0'>
                        <span className='text-red-700'>
                          <Badge variant='outline' className='text-xs mr-2'>{charge.type}</Badge>
                          {charge.description}
                        </span>
                        <span className='font-mono text-red-800'>{formatCurrency(charge.amount)}</span>
                      </div>
                    ))}
                    {summary.overageCharges.length > 5 && (
                      <p className='text-xs text-red-600 mt-1 italic'>+{summary.overageCharges.length - 5} more charges</p>
                    )}
                  </div>
                </div>
              )}
              <Button className='mt-4 bg-red-600 hover:bg-red-700 text-white rounded-sm'
                onClick={handleCreateChangeOrderFromOverage} data-testid='create-co-from-overage'>
                <Plus className='w-4 h-4 mr-2' />Create Change Order from Overage
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Back Button */}
      <Button onClick={() => navigate('/dashboard/recap')} variant='ghost' className='text-slate-600 hover:text-[#1A3668] rounded-sm'>
        <ArrowLeft className='w-4 h-4 mr-2' />Back to Job Recap
      </Button>

      {/* Job Header */}
      <div className='flex flex-col md:flex-row md:items-start md:justify-between gap-4'>
        <div>
          <div className='flex items-center gap-3 mb-2'>
            <h1 className='text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900'>{job.jobNumber}</h1>
            {adjustedIsOverPo ? (
              <Badge className='bg-red-100 text-red-800 border-red-200 rounded-sm text-lg px-3'>
                <AlertTriangle className='w-4 h-4 mr-1' />Over Budget
              </Badge>
            ) : summary.isNearPo ? (
              <Badge className='bg-amber-100 text-amber-800 border-amber-200 rounded-sm text-lg px-3'>
                <AlertTriangle className='w-4 h-4 mr-1' />Near PO Limit
              </Badge>
            ) : (
              <Badge className='bg-green-100 text-green-800 border-green-200 rounded-sm text-lg px-3'>On Track</Badge>
            )}
          </div>
          <p className='text-xl text-slate-700'>{job.description}</p>
          <p className='text-lg text-slate-500 mt-1'>Customer: {job.customerName} | PO: {job.poNumber}</p>
        </div>
      </div>

      {/* PO Utilization Progress */}
      <Card className='border-slate-200 shadow-sm rounded-sm'>
        <CardContent className='p-6'>
          <div className='flex justify-between items-start mb-4'>
            <div>
              <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Total Charges</p>
              <p className='text-3xl font-bold text-slate-900'>{formatCurrency(adjustedTotalWithPending)}</p>
              <p className='text-sm text-slate-500 mt-1'>Original PO Value: {formatCurrency(data?.job?.originalPoValue || summary.poValue)}</p>
              {approvedCOAmount > 0 && (
                <p className='text-sm text-blue-600 mt-1'>
                  <FileText className='w-3 h-3 inline mr-1' />Additional Scope (COs): +{formatCurrency(approvedCOAmount)}
                </p>
              )}
              {pendingCOAmount > 0 && (
                <p className='text-sm text-amber-500 mt-1 italic'>Pending CO Scope: +{formatCurrency(pendingCOAmount)}</p>
              )}
              <p className='text-sm font-bold text-slate-700 mt-1'>Current PO Value: {formatCurrency(summary.poValue)}</p>
            </div>
            <div className='flex flex-col items-end gap-3'>
              <div className='flex items-center gap-2'>
                <Label htmlFor='exclude-nonbillable' className='text-sm text-slate-600 cursor-pointer'>Exclude Non-Billable</Label>
                <Switch id='exclude-nonbillable' checked={excludeNonBillable} onCheckedChange={setExcludeNonBillable}
                  data-testid='exclude-nonbillable-toggle' />
              </div>
              <div className='text-right'>
                <p className='text-sm text-slate-500'>Balance Remaining</p>
                <p className={`text-2xl font-bold ${adjustedPoBalanceWithPending >= 0 ? 'text-[#16A34A]' : 'text-red-600'}`}>
                  {formatCurrency(adjustedPoBalanceWithPending)}
                </p>
                {adjustedPendingAmount > 0 && (
                  <p className='text-xs text-slate-500 italic'>(Pending: {formatCurrency(adjustedPendingAmount)})</p>
                )}
              </div>
            </div>
          </div>
          <div className='mb-2'>
            <div className='flex justify-between text-sm mb-1'>
              <span className='text-slate-600'>PO Utilization</span>
              <span className='font-bold'>{adjustedUtilizationPctWithPending}%</span>
            </div>
          </div>
          <Progress value={Math.min(adjustedUtilizationPctWithPending, 100)}
            className={`h-4 ${adjustedIsOverPo ? '[&>div]:bg-red-500' : adjustedIsNearPo ? '[&>div]:bg-amber-500' : '[&>div]:bg-[#16A34A]'}`} />
          <div className='flex justify-between mt-2 text-sm text-slate-500'>
            <span>$0</span>
            <span>{formatCurrency(summary.poValue)}</span>
          </div>
          {excludeNonBillable && nonBillableMaterialCharges > 0 && (
            <p className='text-xs text-slate-500 mt-2 italic'>
              * Excluding {formatCurrency(nonBillableMaterialCharges)} in non-billable material charges
              {nonBillablePendingAmount > 0 && ` and ${formatCurrency(nonBillablePendingAmount)} in non-billable pending`}
            </p>
          )}

          {/* Invoiced Amount - Office/Owner only */}
          {isOfficeOrOwner && (
            <div className='mt-4 pt-4 border-t border-slate-200'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-3'>
                  <div className='flex items-center gap-2'>
                    <Check className='w-4 h-4 text-[#1A3668]' />
                    <span className='text-sm font-bold uppercase tracking-wider text-slate-500'>Invoiced</span>
                  </div>
                  {editingInvoiced ? (
                    <div className='flex items-center gap-2'>
                      <span className='text-lg text-slate-400'>$</span>
                      <Input
                        type='number' step='0.01' min='0'
                        value={invoicedInput}
                        onChange={(e) => setInvoicedInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveInvoicedAmount()}
                        className='w-40 h-9 rounded-sm font-mono text-lg'
                        data-testid='invoiced-amount-input'
                        autoFocus
                      />
                      <Button size='sm' onClick={handleSaveInvoicedAmount} className='bg-[#1A3668] hover:bg-[#142A52] rounded-sm h-9'>Save</Button>
                      <Button size='sm' variant='ghost' onClick={() => setEditingInvoiced(false)} className='rounded-sm h-9'>Cancel</Button>
                    </div>
                  ) : (
                    <div className='flex items-center gap-2'>
                      <span className='text-2xl font-bold text-[#1A3668]'>{formatCurrency(summary.invoicedAmount || 0)}</span>
                      <span className='text-sm text-slate-400'>of {formatCurrency(adjustedTotalWithPending)}</span>
                      <Button size='sm' variant='ghost' onClick={() => { setInvoicedInput(String(summary.invoicedAmount || 0)); setEditingInvoiced(true); }}
                        className='text-slate-400 hover:text-[#1A3668] h-7 w-7 p-0' data-testid='edit-invoiced-btn'>
                        <Pencil className='w-3.5 h-3.5' />
                      </Button>
                    </div>
                  )}
                </div>
                {!editingInvoiced && (summary.invoicedAmount || 0) > 0 && (
                  <div className='text-right'>
                    <span className='text-sm text-slate-500'>Uninvoiced: </span>
                    <span className='text-sm font-bold text-amber-600'>
                      {formatCurrency(adjustedTotalWithPending - (summary.invoicedAmount || 0))}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4'>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 mb-2'>
              <DollarSign className='w-5 h-5 text-[#1A3668]' />
              <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>PO Value</p>
            </div>
            <p className='text-3xl font-bold text-[#1A3668]'>{formatCurrency(summary.poValue)}</p>
          </CardContent>
        </Card>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 mb-2'>
              <Users className='w-5 h-5 text-blue-600' />
              <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Labor</p>
            </div>
            <p className='text-3xl font-bold text-blue-600'>{formatCurrency(summary.laborCharges)}</p>
            <p className='text-sm text-slate-500 mt-1'>{labor.totalHours?.toFixed(1)} hrs</p>
          </CardContent>
        </Card>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 mb-2'>
              <Package className='w-5 h-5 text-purple-600' />
              <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Material</p>
            </div>
            <p className='text-3xl font-bold text-purple-600'>{formatCurrency(adjustedMaterialCharges)}</p>
            <p className='text-sm text-slate-500 mt-1'>
              {material.entryCount} invoices
              {excludeNonBillable && nonBillableMaterialCharges > 0 && ' (excl. non-billable)'}
            </p>
          </CardContent>
        </Card>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 mb-2'>
              <Truck className='w-5 h-5 text-amber-600' />
              <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Equipment</p>
            </div>
            <p className='text-3xl font-bold text-amber-600'>{formatCurrency(summary.equipmentCharges || 0)}</p>
            <p className='text-sm text-slate-500 mt-1'>{equipment?.totalHours?.toFixed(1) || 0} hrs</p>
          </CardContent>
        </Card>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 mb-2'>
              <TrendingUp className='w-5 h-5 text-[#16A34A]' />
              <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Total</p>
            </div>
            <p className='text-3xl font-bold text-[#16A34A]'>{formatCurrency(adjustedTotalCharges)}</p>
            {adjustedPendingAmount > 0 && (
              <p className='text-xs text-amber-500 mt-1'>+{formatCurrency(adjustedPendingAmount)} pending</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detailed Tables */}
      <Tabs defaultValue='labor' className='w-full'>
        <TabsList className={`flex w-full rounded-sm`}>
          <TabsTrigger value='labor' className='flex-1 rounded-sm'><Users className='w-4 h-4 mr-2' />Labor ({labor.entryCount})</TabsTrigger>
          <TabsTrigger value='material' className='flex-1 rounded-sm'><Package className='w-4 h-4 mr-2' />Material ({material.entryCount})</TabsTrigger>
          <TabsTrigger value='equipment' className='flex-1 rounded-sm'><Truck className='w-4 h-4 mr-2' />Equipment ({equipment?.entryCount || 0})</TabsTrigger>
          <TabsTrigger value='pending' className='flex-1 rounded-sm'><Clock className='w-4 h-4 mr-2' />Pending ({pending?.count || 0})</TabsTrigger>
          <TabsTrigger value='change-orders' className='flex-1 rounded-sm'><FileText className='w-4 h-4 mr-2' />COs ({changeOrders.length})</TabsTrigger>
          <TabsTrigger value='cross-charges' className='flex-1 rounded-sm'>
            <ArrowRightLeft className='w-4 h-4 mr-2' />Cross Charges ({(data?.crossCharges?.outgoingCount || 0) + (data?.crossCharges?.incomingCount || 0)})
          </TabsTrigger>
        </TabsList>

        <TabsContent value='labor'>
          <LaborTab labor={labor} data={data} selectedLaborIds={selectedLaborIds}
            setSelectedLaborIds={setSelectedLaborIds} onMoveClick={() => setMoveDialogOpen(true)} jobId={id} />
        </TabsContent>
        <TabsContent value='material'>
          <MaterialTab material={material} pending={pending} data={data} />
        </TabsContent>
        <TabsContent value='equipment'>
          <EquipmentTab equipment={equipment} pending={pending} />
        </TabsContent>
        <TabsContent value='pending'>
          <PendingTab pending={pending} summary={summary} data={data} />
        </TabsContent>
        <TabsContent value='change-orders'>
          <ChangeOrdersTab changeOrders={changeOrders} approvedCOAmount={approvedCOAmount} pendingCOAmount={pendingCOAmount} />
        </TabsContent>
        <TabsContent value='cross-charges'>
          <CrossChargesTab crossCharges={data?.crossCharges} />
        </TabsContent>
      </Tabs>

      {/* Move Labor Dialog */}
      <MoveLaborDialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}
        selectedCount={selectedLaborIds.length} selectedAmount={selectedLaborAmount}
        jobs={jobs} targetJobId={moveTargetJobId} setTargetJobId={setMoveTargetJobId}
        reason={moveReason} setReason={setMoveReason}
        onConfirm={handleMoveLaborEntries} isMoving={movingLabor} />
    </div>
  );
}
